import React, { MouseEvent as ReactMouseEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ng } from "../../ngds";
import { NGDropdownListItem, SXDropdown, SXDropdownSection } from "./SXDropdown";
import { IMaterial, Tooltip, TooltipPosition } from "./SXMaterialTooltips";
import {
  CountryGroupableMaterial,
  groupMaterialsByCountry,
  sortMaterialsByName,
} from "../../helpers/materialCountries";

export interface SXReinforcementMaterialDropdownProps {
  /** `id` of the toggle button. */
  id?: string;
  /** `name` of the toggle; also the prefix of every item's `id` (`<name>_option_…`). */
  name: string;
  /** Materials to offer. Grouped under country headings when they carry an `annex`. */
  materials: readonly CountryGroupableMaterial[];
  selectedGuid: string | null | undefined;
  onSelect: (guid: string) => void;
  /**
   * Fetches the full material for the stress-strain popup shown on hover. Each app has its own
   * route for this, so it is injected. Resolve `null` when it cannot be had; it is retried on the
   * next hover.
   */
  loadMaterial: (guid: string) => Promise<ng.material.Data | null | undefined>;
  disabled?: boolean;
}

// An SVG rather than "✓", which not every UI font has.
const SelectedCheck = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-label="selected" style={{ marginLeft: 12, flexShrink: 0 }}>
    <path d="M2 6.5 L5 9.5 L10 3" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

/**
 * Reinforcement material picker: the materials grouped under non-selectable country headings, and
 * the material's stress-strain curve in a popup while an item is hovered.
 */
export const SXReinforcementMaterialDropdown = (props: SXReinforcementMaterialDropdownProps) => {
  const { t, i18n } = useTranslation();
  const { materials, selectedGuid, onSelect, loadMaterial, name } = props;

  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [hoveredMaterial, setHoveredMaterial] = useState<IMaterial | null>(null);
  const cache = useRef<Record<string, IMaterial>>({});
  // A slow fetch must not open the popup for an item the pointer has already left.
  const hoveredGuid = useRef<string | null>(null);

  const { list, sections } = useMemo(() => {
    const toItem = (m: CountryGroupableMaterial): NGDropdownListItem<string> => ({ id: m.guid, name: m.name });
    const groups = groupMaterialsByCountry(materials, (key) => t(key), i18n.language);
    if (!groups) {
      return { list: sortMaterialsByName(materials, i18n.language).map(toItem), sections: undefined };
    }
    const grouped: SXDropdownSection<string>[] = groups.map((group, index) => ({
      header: t(group.label),
      items: group.items.map(toItem),
      itemIdPrefix: `${name}_option_${index}`,
    }));
    return { list: grouped.flatMap((section) => section.items), sections: grouped };
  }, [materials, name, t, i18n.language]);

  // The same name exists in several countries (B500B: Sweden, Norway, Great Britain), so the closed
  // dropdown names the country too. Only the toggle gets it; the items sit under their heading already.
  const selected = useMemo((): NGDropdownListItem<string> | null => {
    const item = list.find((entry) => entry.id === selectedGuid);
    if (!item) {
      return null;
    }
    const country = sections?.find((section) => section.items.includes(item))?.header;
    return country
      ? {
          ...item,
          raw: (
            <>
              {item.name} <i>({country})</i>
            </>
          ),
        }
      : item;
  }, [list, sections, selectedGuid]);

  const handleMouseOver = async (e: ReactMouseEvent, item?: NGDropdownListItem<string>): Promise<void> => {
    if (!item) {
      return;
    }
    hoveredGuid.current = item.id;
    setTooltipPosition({ clientX: e.clientX, clientY: e.clientY });

    const cached = cache.current[item.id];
    if (cached) {
      setHoveredMaterial(cached);
      return;
    }
    setHoveredMaterial(null);
    const mtrl = await loadMaterial(item.id).catch(() => null);
    if (!mtrl) {
      return;
    }
    const entry: IMaterial = { id: item.id, name: item.name, mtrl };
    cache.current[item.id] = entry;
    if (hoveredGuid.current === item.id) {
      setHoveredMaterial(entry);
    }
  };

  const handleMouseOut = (): void => {
    hoveredGuid.current = null;
    setTooltipPosition(null);
    setHoveredMaterial(null);
  };

  return (
    <>
      <Tooltip tooltipPosition={tooltipPosition} hoverdMtrl={hoveredMaterial} />
      <SXDropdown
        id={props.id}
        name={name}
        list={list}
        sections={sections}
        collapsibleSections
        renderItemSuffix={(item) => (item.id === selectedGuid ? <SelectedCheck /> : null)}
        val={selected}
        lang={i18n.language}
        localisation={false}
        notSelectedName={"select_.label"}
        onMouseOverItem={(e, item) => void handleMouseOver(e, item)}
        onMouseOutItem={handleMouseOut}
        disabled={props.disabled || list.length === 0}
        onUpdate={(value) => {
          if (value?.id) {
            onSelect(value.id);
          }
        }}
      />
    </>
  );
};
